import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { DotsThree } from "@phosphor-icons/react";

export type MenuItem = {
  label: ReactNode;
  onClick: () => void;
  danger?: boolean;
  testid?: string;
  disabled?: boolean;
};

/** "⋯" overflow menu — holds everything beyond a card/row's 2–3 visible actions. */
export function OverflowMenu({ items, testid, disabled = false }: {
  items: MenuItem[];
  testid?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [open]);
  if (items.length === 0) return null;
  return (
    <div className="menu-pop" ref={ref}>
      <button
        type="button"
        className="btn ghost icon"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More actions"
        data-testid={testid}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      ><DotsThree weight="bold" /></button>
      {open && (
        <div className="pop" role="menu">
          {items.map((item, i) => (
            <button
              key={i}
              type="button"
              role="menuitem"
              className={item.danger ? "danger" : ""}
              data-testid={item.testid}
              disabled={item.disabled}
              onClick={() => { setOpen(false); item.onClick(); }}
            >{item.label}</button>
          ))}
        </div>
      )}
    </div>
  );
}
